import { Currency } from "dinero.js"
import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../currency/useMoney"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useNumber } from "../../../../../localization/useNumber"

import { AdjustmentDirection, PriceAdjustmentType } from "../../priceListTypes"
import {
    AdjustPricesFormValues,
    buildRoundingVars,
    formatAdjustmentValue,
    ROUNDING_SUMMARY_KEYS,
} from "./adjustPricesLib"

const useStyles = makeStyles()((theme) => ({
    row: {
        paddingBottom: ".5rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    label: {
        color: theme.palette.tailwind.slate[500],
    },
    value: {
        fontWeight: 700,
    },
    increase: {
        color: theme.palette.success.dark,
    },
    reduction: {
        color: theme.palette.error.dark,
    },
}))

type AdjustmentSummaryProps = Readonly<{
    values: AdjustPricesFormValues
    itemCount: number
    currency: Currency<number>
}>

export function AdjustmentSummary({ values, itemCount, currency }: AdjustmentSummaryProps) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})
    const numberFactory = useNumber({})
    const roundingVars = buildRoundingVars(moneyFactory, currency, values.rounding)

    const sign = values.adjustmentType === PriceAdjustmentType.INCREASE ? "+" : "-"
    const value =
        values.value === ""
            ? undefined
            : formatAdjustmentValue(moneyFactory, numberFactory, values.unit, values.value, currency)
    const directionClasses: Record<AdjustmentDirection, string> = {
        [PriceAdjustmentType.INCREASE]: classes.increase,
        [PriceAdjustmentType.REDUCTION]: classes.reduction,
    }

    return (
        <Stack spacing=".75rem">
            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.row}>
                <LocalizedStrict id="adjust-price-list-summary-change">
                    <Typography variant="body2" className={classes.label}>
                        Change
                    </Typography>
                </LocalizedStrict>
                {value === undefined ? (
                    <Typography variant="body2" className={classes.value}>
                        –
                    </Typography>
                ) : (
                    <LocalizedStrict id="adjust-price-list-summary-change-value" vars={{ sign, value }}>
                        <Typography
                            variant="body2"
                            className={cx(classes.value, directionClasses[values.adjustmentType])}
                        >
                            {sign}
                            {value}
                        </Typography>
                    </LocalizedStrict>
                )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.row}>
                <LocalizedStrict id="adjust-price-list-summary-items">
                    <Typography variant="body2" className={classes.label}>
                        Items affected
                    </Typography>
                </LocalizedStrict>
                <Typography variant="body2" className={classes.value}>
                    {itemCount}
                </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.row}>
                <LocalizedStrict id="adjust-price-list-summary-rounding">
                    <Typography variant="body2" className={classes.label}>
                        Rounding
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id={ROUNDING_SUMMARY_KEYS[values.rounding]} vars={roundingVars}>
                    <Typography variant="body2" className={classes.value}>
                        {values.rounding}
                    </Typography>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
